/**
 * Detects the most likely CSV delimiter from the first line of a file's content.
 * Checks for comma, semicolon, tab, and pipe by counting occurrences.
 * Falls back to comma if none are found.
 *
 * @param {string} content - Raw text content (only the first line is used)
 * @returns {string} The detected delimiter character
 */
export function detectCsvDelimiter(content) {
  const firstLine = content.split('\n')[0];
  const candidates = [',', ';', '\t', '|'];
  let maxCount = 0;
  let detected = ',';
  for (const c of candidates) {
    const escaped = c === '|' ? '\\|' : c === '\t' ? '\t' : c;
    const count = (firstLine.match(new RegExp(escaped, 'g')) || []).length;
    if (count > maxCount) { maxCount = count; detected = c; }
  }
  return detected;
}

/**
 * Normalizes a string for fuzzy header matching:
 * - Lowercases
 * - Collapses hyphens, underscores, dots, and whitespace runs into a single space
 * - Trims leading/trailing whitespace
 *
 * Examples:
 *   "First-Name"  → "first name"
 *   "first_name"  → "first name"
 *   "First Name"  → "first name"
 *   "FIRSTNAME"   → "firstname"
 */
export function normalizeHeader(str) {
  if (typeof str !== "string") return "";
  return str.toLowerCase().trim().replace(/[-_./\s]+/g, " ").trim();
}

function significantTokens(normalizedStr) {
  return normalizedStr.split(" ").filter(t => t.length > 2);
}

function tokensMatch(a, b) {
  if (a === b) return true;
  // Allow prefix matching for inflected forms (e.g. German "höherwertige" vs "höherwertiger")
  if (a.length >= 6 && b.length >= 6) {
    return a.startsWith(b) || b.startsWith(a);
  }
  return false;
}

function buildAliasSet(field, translateFn) {
  const raw = [];

  const vfs = field?.values_for_selected;
  if (typeof vfs === "string" && vfs.trim().length > 0) {
    raw.push(...vfs.split(",").map(v => v.trim()).filter(Boolean));
  }

  if (typeof field?.label === "string" && field.label.trim().length > 0) {
    raw.push(field.label.trim());
  }

  if (typeof translateFn === "function" && field?.translation_key) {
    const translated = translateFn(field.translation_key, field.label);
    if (typeof translated === "string" && translated.trim().length > 0) {
      raw.push(translated.trim());
    }
  }

  const seen = new Set();
  for (const alias of raw) {
    const n = normalizeHeader(alias);
    if (n.length > 0) seen.add(n);
  }
  return seen;
}

const MIN_CONTAINS_LEN = 5;
const MIN_CONTAINS_RATIO = 0.4;
const TOKEN_OVERLAP_RATIO = 0.6;
const TIER_COUNT = 3;

/**
 * Performs tiered fuzzy matching between CSV headers and field definitions.
 *
 * Tier 1 (Exact normalized):   normalize(header) === alias
 * Tier 2 (Contains):           alias.includes(normHeader) or normHeader.includes(alias)
 *                               — both sides must be >= 5 chars and length ratio >= 40%
 * Tier 3 (Token overlap):      significant tokens (len > 2) overlap at >= 60%
 *                               of the smaller set; require >= 2 overlapping tokens
 *                               when both sets have >= 2 tokens
 *
 * @param {Array} fields - field definitions from API (with values_for_selected, label, translation_key)
 * @param {string[]} headers - raw CSV column names
 * @param {(key: string, alt: string) => string} translateFn - translate() from useTranslation (returns string, not JSX)
 * @returns {{ [fieldKey: string]: string }}
 */
export function autoAssignHeaders(fields, headers, translateFn) {
  if (!Array.isArray(fields) || fields.length === 0) return {};
  if (!Array.isArray(headers) || headers.length === 0) return {};

  const fieldAliases = fields.map(field => ({
    key: field.key,
    aliases: buildAliasSet(field, translateFn),
  }));

  // fieldKey → { header, tier } for the best match found so far
  const bestMatchPerField = new Map();
  const bestTierPerField = new Map();

  for (const header of headers) {
    const normHeader = normalizeHeader(header);
    if (normHeader.length === 0) continue;

    let bestTierThisHeader = TIER_COUNT + 1;
    let bestFieldKeyThisHeader = null;

    for (const { key, aliases } of fieldAliases) {
      const existingTier = bestTierPerField.get(key) ?? (TIER_COUNT + 1);
      if (existingTier === 1) continue; // already perfect match, skip

      let matchTier = TIER_COUNT + 1;

      // Tier 1: Exact normalized match
      for (const alias of aliases) {
        if (normHeader === alias) {
          matchTier = 1;
          break;
        }
      }

      // Tier 2: Contains (bidirectional) with length guards
      if (matchTier > 2 && normHeader.length >= MIN_CONTAINS_LEN) {
        for (const alias of aliases) {
          if (alias.length >= MIN_CONTAINS_LEN) {
            const shorter = Math.min(alias.length, normHeader.length);
            const longer = Math.max(alias.length, normHeader.length);
            if (shorter / longer >= MIN_CONTAINS_RATIO) {
              if (alias.includes(normHeader) || normHeader.includes(alias)) {
                matchTier = 2;
                break;
              }
            }
          }
        }
      }

      // Tier 3: Token overlap
      if (matchTier > 3) {
        const headerTokens = significantTokens(normHeader);
        if (headerTokens.length > 0) {
          for (const alias of aliases) {
            const aliasTokens = significantTokens(alias);
            if (aliasTokens.length === 0) continue;
            const overlap = headerTokens.filter(ht => aliasTokens.some(at => tokensMatch(ht, at)));
            const smaller = Math.min(headerTokens.length, aliasTokens.length);
            const minOverlapCount =
              headerTokens.length >= 2 && aliasTokens.length >= 2 ? 2 : 1;
            if (
              overlap.length >= minOverlapCount &&
              overlap.length / smaller >= TOKEN_OVERLAP_RATIO
            ) {
              matchTier = 3;
              break;
            }
          }
        }
      }

      if (matchTier <= TIER_COUNT && matchTier < bestTierThisHeader) {
        bestTierThisHeader = matchTier;
        bestFieldKeyThisHeader = key;
      }
    }

    if (bestFieldKeyThisHeader !== null) {
      const existingTier = bestTierPerField.get(bestFieldKeyThisHeader) ?? (TIER_COUNT + 1);
      if (bestTierThisHeader < existingTier) {
        bestMatchPerField.set(bestFieldKeyThisHeader, { header, tier: bestTierThisHeader });
        bestTierPerField.set(bestFieldKeyThisHeader, bestTierThisHeader);
      }
    }
  }

  const result = {};
  for (const [fieldKey, { header }] of bestMatchPerField) {
    result[fieldKey] = header;
  }
  return result;
}
